import React, { useMemo, useState } from "react";
import { Info } from "lucide-react";
import { visibleProposedTimes } from "./visibleProposedTimes";
import { fmtRange } from "./proposalRange";
import { startOfWeek } from "./NetworkCalendarGrid";

// ── Clinic Month view (2026-08, replaces the old List view) ─────────────────
// Full-month grid, Sunday-start weeks (standing calendar rule). Each day
// cell shows compact chips for that day's items — confirmed appointments and
// pending-request windows. Chips are colored per location (locationColors,
// multi-location users only) and ALWAYS carry a short location label next to
// the hue — color is never the only signal. Empty cells are inert; no
// availability styling (the standing calendar rule).
//
// Affordance mapping (one mechanism, nothing unreachable):
// - pending chip body click  → onSpotlight(request)  (THE SAME
//   activateSpotlight the old card click and the left-nav toggle call —
//   third entrance, no parallel implementation)
// - non-pending chip click   → onOpen(request)        (existing detail modal)
// - pending chip ⓘ           → onOpen(request)        (detail stays reachable)
// - "+N more"                → expands the cell in place
//
// SHARED SOURCE (2026-08): this is the ONE month-grid implementation,
// consumed by BOTH the clinic portal (ClinicScheduling — default props:
// location color mode, byte-identical rendering) and the internal Network
// Scheduling tab (chipColorMode="status": status is the primary visual;
// internal passes no location color/label maps — one selected clinic on
// screen, matching the week view's legend). Portal differences are props
// only — never fork this file.

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmt12(hms) {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m}${hr >= 12 ? "p" : "a"}`;
}

const STATUS_DOT = {
  pending: "bg-amber-500",
  suggested_alternative: "bg-orange-500",
  locked_in: "bg-emerald-500",
  direct_booking: "bg-indigo-500",
};

const MAX_CHIPS = 3;

// Internal ("status") chip color mode: status is the primary visual —
// hues match the internal Pending / Alt Suggested / Locked In legend.
const STATUS_CHIP = {
  pending: "bg-blue-100 text-blue-900 border-blue-300",
  suggested_alternative: "bg-amber-100 text-amber-900 border-amber-300",
  locked_in: "bg-emerald-100 text-emerald-900 border-emerald-200",
  direct_booking: "bg-indigo-100 text-indigo-900 border-indigo-300",
};

export function ClinicMonthView({
  monthAnchor,          // Date — any day in the rendered month
  requests,
  onOpen,
  onSpotlight,
  highlightRequestId = null,
  locationColors = null, // { code: { color, suffix } } — multi-location only
  locationLabels = null, // { code: shortLabel }
  chipColorMode = "location", // "location" (clinic) | "status" (internal)
  rootTestId = "clinic-month-view",
  // Reserved WestStar slots (Phase 2, default off) — rendered as extra
  // slate-dashed chips in each day cell. See NetworkCalendarGrid for the
  // shared design; here they read as low-emphasis "capacity markers".
  reservedSlots = [],
  reservedMode = "none",
  onReservedClick,
}) {
  const [expanded, setExpanded] = useState({}); // { iso: true }

  const monthIdx = monthAnchor.getMonth();
  const gridStart = useMemo(
    () => startOfWeek(new Date(monthAnchor.getFullYear(), monthIdx, 1)),
    [monthAnchor, monthIdx]
  );
  const days = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
    [gridStart]
  );

  const byDate = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[toISODate(d)] = []; });
    (requests || []).forEach((r) => {
      // Confirmed-sibling suppression: same shared helper as every other
      // calendar surface — locked_in renders only its confirmed time.
      visibleProposedTimes(r).forEach((p) => {
        if (map[p.date] !== undefined) map[p.date].push({ request: r, time: p });
      });
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => a.time.start_time.localeCompare(b.time.start_time))
    );
    return map;
  }, [requests, days]);

  // Reserved slots (default []): grouped per ISO date. Booked slots
  // skipped (rendered elsewhere as Direct chips). Past = grayed inert on
  // both portals.
  const nowRef = useMemo(() => {
    const n = new Date();
    const iso = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    const t = `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}:${String(n.getSeconds()).padStart(2, "0")}`;
    return { iso, t };
  }, [monthAnchor]);

  const isReservedPast = (r) => {
    if (!r?.date || !r?.slot_time) return false;
    if (r.date < nowRef.iso) return true;
    if (r.date > nowRef.iso) return false;
    const st = r.slot_time.length === 5 ? `${r.slot_time}:00` : r.slot_time;
    return st <= nowRef.t;
  };

  const reservedByDate = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[toISODate(d)] = []; });
    (reservedSlots || []).forEach((r) => {
      if (r.state === "booked") return;
      if (map[r.date] === undefined) return;
      map[r.date].push(r);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (a.slot_time || "").localeCompare(b.slot_time || ""))
    );
    return map;
  }, [reservedSlots, days]);

  const todayISO = toISODate(new Date());

  return (
    <div data-testid={rootTestId}>
      <div className="grid grid-cols-7 border-l border-t border-slate-200 rounded-lg overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <div key={w} className="px-2 py-1.5 bg-slate-50 border-r border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500 font-semibold text-center">
            {w}
          </div>
        ))}
        {days.map((d) => {
          const iso = toISODate(d);
          const items = byDate[iso] || [];
          const inMonth = d.getMonth() === monthIdx;
          const isToday = iso === todayISO;
          const isExpanded = !!expanded[iso];
          const shown = isExpanded ? items : items.slice(0, MAX_CHIPS);
          const overflow = items.length - shown.length;
          return (
            <div
              key={iso}
              data-testid={`month-cell-${iso}`}
              className={`min-h-[92px] border-r border-b border-slate-200 p-1 ${
                inMonth ? "bg-white" : "bg-slate-50/60"
              }`}
            >
              <div className={`text-[11px] font-semibold mb-0.5 px-0.5 ${
                isToday
                  ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white"
                  : inMonth ? "text-slate-700" : "text-slate-400"
              }`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {(reservedByDate[iso] || []).map((r) => {
                  const past = isReservedPast(r);
                  const isOpen = r.state === "open";
                  const isBlocked = r.state === "blocked";
                  const clickable =
                    !past &&
                    ((reservedMode === "clinic" && (isOpen || isBlocked)) ||
                      (reservedMode === "internal" && isOpen));
                  const label = `WS · ${fmt12(r.slot_time)}`;
                  const st = r.slot_time.length === 5 ? `${r.slot_time}:00` : r.slot_time;
                  const cls = past
                    ? "border-slate-300 bg-slate-50 text-slate-400"
                    : isBlocked
                    ? "border-slate-400 bg-slate-100 text-slate-500 line-through"
                    : "border-slate-400 bg-slate-50 text-slate-700";
                  const hover =
                    !past && !isBlocked && reservedMode === "internal"
                      ? "hover:bg-blue-50 hover:border-blue-500 hover:text-blue-900"
                      : !past && reservedMode === "clinic"
                      ? "hover:border-slate-600"
                      : "";
                  return (
                    <div
                      key={`res-${st}-${r.date}`}
                      data-testid={`month-reserved-${iso}-${st}`}
                      data-state={past ? "expired" : r.state}
                      role={clickable ? "button" : "img"}
                      tabIndex={clickable ? 0 : -1}
                      onClick={clickable && onReservedClick ? () => onReservedClick(r) : undefined}
                      title={
                        past
                          ? `${label} · past`
                          : isBlocked
                          ? `${label} · blocked${reservedMode === "clinic" ? " · click to unblock" : ""}`
                          : reservedMode === "internal"
                          ? `${label} · open · click to claim`
                          : reservedMode === "clinic"
                          ? `${label} · open · click to block`
                          : `${label} · open`
                      }
                      className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded border border-dashed truncate ${cls} ${hover} ${
                        clickable ? "cursor-pointer" : "cursor-default"
                      }`}
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(45deg, rgba(148,163,184,0.18) 0 4px, transparent 4px 8px)",
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
                {shown.map(({ request, time }) => {
                  const isPending = request.status === "pending";
                  const loc = locationColors?.[request.location_code];
                  const label = locationLabels?.[request.location_code];
                  const chipCls =
                    chipColorMode === "status"
                      ? STATUS_CHIP[request.status] || STATUS_CHIP.pending
                      : loc
                      ? loc.color.chip
                      : "bg-slate-100 text-slate-800 border-slate-300";
                  // status mode: location = secondary stripe (+ the label
                  // below — never color alone). Undefined in location mode,
                  // so the clinic DOM stays byte-identical.
                  const stripeStyle =
                    chipColorMode === "status" && loc
                      ? { borderLeft: `3px solid ${loc.color.stripe}` }
                      : undefined;
                  // B.1 (2026-06): when the caller supplies location TINTS
                  // (clinic portal), the chip paints the same hue the week
                  // grid paints — one hue per clinic across both views.
                  const tintStyle =
                    chipColorMode !== "status" && loc?.tint
                      ? { backgroundColor: loc.tint.tint, color: loc.tint.text, borderColor: loc.tint.border }
                      : undefined;
                  return (
                    <div
                      key={`${request.id}-${time.id}`}
                      role="button"
                      tabIndex={0}
                      style={{ ...(stripeStyle || {}), ...(tintStyle || {}) }}
                      data-testid={`month-chip-${request.id}-${time.id}`}
                      onClick={() =>
                        isPending && onSpotlight ? onSpotlight(request) : onOpen?.(request)
                      }
                      className={`relative w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded border cursor-pointer truncate hover:shadow-sm transition ${chipCls} ${
                        request.id === highlightRequestId ? "ring-2 ring-blue-500 ring-offset-1" : ""
                      }`}
                      title={`${request.patient_name || `Patient #${request.patient_id}`} · ${fmtRange(time)}${label ? ` · ${label}` : ""}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${STATUS_DOT[request.status] || STATUS_DOT.pending}`} />
                      <span className="font-semibold">{fmt12(time.start_time)}</span>{" "}
                      <span className="opacity-90">{request.patient_name || `#${request.patient_id}`}</span>
                      {request.type === "direct_booking" && (
                        <span data-testid={`month-chip-direct-${request.id}-${time.id}`} className="ml-1 font-medium opacity-90">
                          · Direct
                        </span>
                      )}
                      {label && (
                        <span data-testid={`month-chip-loc-${request.id}-${time.id}`} className="ml-1 font-medium opacity-80">
                          · {label}{loc?.suffix || ""}
                        </span>
                      )}
                      {isPending && (
                        <button
                          type="button"
                          data-testid={`month-chip-details-${request.id}-${time.id}`}
                          title="Open request details"
                          onClick={(e) => { e.stopPropagation(); onOpen?.(request); }}
                          className="absolute top-0.5 right-0.5 p-0.5 text-slate-400 hover:text-blue-600"
                        >
                          <Info size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    data-testid={`month-more-${iso}`}
                    onClick={() => setExpanded((e) => ({ ...e, [iso]: true }))}
                    className="w-full text-left text-[10px] font-semibold text-blue-600 hover:text-blue-800 px-1.5"
                  >
                    +{overflow} more
                  </button>
                )}
                {isExpanded && items.length > MAX_CHIPS && (
                  <button
                    type="button"
                    data-testid={`month-less-${iso}`}
                    onClick={() => setExpanded((e) => ({ ...e, [iso]: false }))}
                    className="w-full text-left text-[10px] font-semibold text-slate-500 hover:text-slate-700 px-1.5"
                  >
                    show less
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
